import React, { useEffect, useContext, useState, useRef } from "react";
import { SimpleTooltip, SimpleUtility } from "simple-ui-components";

import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import MapIcon from "@mui/icons-material/Map";
import ViewListIcon from "@mui/icons-material/ViewList";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";

import { AuthContext } from "../authContextProvider";
import { get, del, post } from "../../server-requests/serverRequests";
import {
  BoxStyled,
  CardStyled,
  CardHeader,
  CardBody,
  BodyWrapperStyled,
  OuterWrapper,
} from "../../theme";
import { LoadingList, LoadingBox } from "../shared/loading";

import { Maps } from "../shared/map";

import { EquipmentDetailPopup } from "./equipmentDetailPopup";
import { FilterEquipment } from "./filterEquipment";
import { AddEditEquipment } from "../equipments/addEditEquipment";
import { Layout, useWindowSize } from "../layout/otherPage";
import DeletePopup from "../shared/deletePopup";
import EquipmentsDetailDashboard from "./equipmentsDetailDashboard";
import { getTranslation, LanguageContext } from "../languageProvider";
import { Log, ReplaceWord } from "../shared/utility";

export const getFilteredItem = (list, searchList) => {
  let listToReturn = [];
  for (let search of searchList) {
    let word = search.trim().toUpperCase();
    if (word) {
      let currentItem = list.filter((item) => {
        let text = item.observations ? item.observations.toUpperCase() : "";
        return text.includes(word);
      });
      for (let result of currentItem) {
        let isExist = listToReturn.some((item) => item.id === result.id);
        if (!isExist) {
          listToReturn = [...listToReturn, ...currentItem];
        }
      }
    }
  }
  return listToReturn;
};

export const Dashboard = () => {
  const equipmentDetailRef = useRef();
  const addEditEquipmentRef = useRef();
  const deleteEquipmentRef = useRef();

  const { Tooltip } = SimpleTooltip;
  const { GetIndexBy } = SimpleUtility;

  const { wrapperHeight, boxHeight } = useWindowSize();

  const { snackbarHandler } = useContext(AuthContext);
  const { lang } = useContext(LanguageContext);

  const [view, setView] = useState("list");

  const [reloadEquipment, setReloadEquipment] = useState(true);
  const [equipmentsList, setEquipmentList] = useState([]);
  const [equipmentListFull, setEquipmentListFull] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [filterEquipment, setFilterEquipment] = useState(false);

  const [marker, setMarker] = useState([]);

  const [mapReload, setMapReload] = useState(true);

  //filter by
  const [filter, setFilter] = useState({});

  const [clientList, setClientList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [iconList, setIconList] = useState([]);

  // get Icon List
  useEffect(() => {
    let isMounted = true;
    get("equipments/icons/list")
      .then((icons) => {
        if (isMounted) {
          setIconList(icons);
        }
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get equipments/icons/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // get State List
  useEffect(() => {
    let isMounted = true;
    get("equipments/states/list")
      .then((state) => {
        if (isMounted) {
          let formattedState = [];
          if (state.length > 0)
            formattedState = state.map((item) => ({
              label: item.name,
              ...item,
              color: `#${item.color}`,
            }));
          setStateList(formattedState);
        }
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get equipments/states/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // get clients list
  useEffect(() => {
    let isMounted = true;
    get("clients/list")
      .then((client) => {
        if (isMounted) {
          let formattedClient = [];
          if (client.length > 0)
            formattedClient = client.map((item) => ({
              label: item.name,
              companyName: item.company.name,
              ...item,
            }));
          setClientList(formattedClient);
        }
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get clients/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // filter equipment
  useEffect(() => {
    if (filterEquipment) {
      let list = [...equipmentListFull];
      if (filter.serialNumber && filter.serialNumber !== "") {
        list = list.filter((item) => {
          let text = item.serialNumber ? item.serialNumber.toUpperCase() : "";
          return text.includes(filter.serialNumber.toUpperCase());
        });
      }
      if (filter.observations && filter.observations !== "") {
        let searchList = filter.observations.split(",");
        list = getFilteredItem(list, searchList);
      }
      if (filter.brandId) {
        list = list.filter((item) => item.model.brand.id === filter.brandId);
      }
      if (filter.modelId) {
        list = list.filter((item) => item.model.id === filter.modelId);
      }
      if (filter.clientId) {
        if (filter.clientId !== "0") {
          list = list.filter(
            (item) => item.client && item.client.id === filter.clientId
          );
        } else {
          list = list.filter((item) => !item.client);
        }
      }
      if (filter.stateId) {
        list = list.filter((item) => item.state.id === filter.stateId);
      }
      setEquipmentList(list);
      setLoadingEquipment(false);
      setFilterEquipment(false);
    }
  }, [filterEquipment, equipmentListFull, filter]);

  // get Equipments List
  useEffect(() => {
    let isMounted = true;
    if (reloadEquipment) {
      get("equipments/list")
        .then((equipment) => {
          if (isMounted) {
            setEquipmentListFull(equipment);
            setReloadEquipment(false);

            setLoadingEquipment(true);
            setFilterEquipment(true);
          }
        })
        .catch((status) => {
          setReloadEquipment(false);
          setLoadingEquipment(false);
          snackbarHandler(getTranslation(`error${status}`));
          Log("Error: get equipments/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadEquipment, snackbarHandler]);

  // set Marker
  useEffect(() => {
    if (view === "map") {
      let equipmentsMarkersList = [];
      if (equipmentsList.length > 0) {
        equipmentsMarkersList = equipmentsList.map((equipment) => ({
          ...equipment,
          pos: {
            lat: Number(equipment.latitude),
            lng: Number(equipment.longitude),
          },
        }));
      }
      setMarker(equipmentsMarkersList);
    }
  }, [view, equipmentsList]);

  const equipmentSelectHandler = (equipment) =>
    equipmentDetailRef.current.Detail(equipment);

  const listViewClickHandler = () => setView("list");
  const mapViewClickHandler = () => setView("map");

  const filterEquipmentChangeHandler = (filterBy) => {
    if (Object.keys(filterBy).length !== 0) {
      setFilter(filterBy);
      setLoadingEquipment(true);
      setFilterEquipment(true);
    }
  };

  const editEquipmentClickHandler = (equipment) =>
    addEditEquipmentRef.current.AddEditEquipmentHandler("edit", equipment);

  const updateEquipmentSuccessHandler = () => setReloadEquipment(true);

  const myLocationClickHandler = (pos) => {
    let newMarker = marker;
    let index = GetIndexBy(marker, 10, "id");
    if (index === -1) {
      setMarker([...newMarker, { id: 10, pos: pos }]);
    } else {
      newMarker[index] = { id: 10, pos: pos };
      setMarker(newMarker);
    }
    setMapReload((prev) => !prev);
  };

  const deleteEquipmentClickHandler = (id) =>
    deleteEquipmentRef.current.Delete(
      id,
      `${getTranslation("0032")} ${
        getTranslation("0071") && getTranslation("0071").toLowerCase()
      }`,
      getTranslation("0120") &&
        ReplaceWord(
          getTranslation("0120"),
          "**NAME**",
          getTranslation("0071").toLowerCase()
        )
    );

  const deleteEquipmentHandler = (id) => {
    del("equipments", {
      id,
    })
      .then(() => {
        snackbarHandler(
          getTranslation("0121") &&
            ReplaceWord(
              getTranslation("0121"),
              "**NAME**",
              getTranslation("0071")
            ),
          "success"
        );
        setReloadEquipment(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: delete equipments" + status);
      });
  };

  const copyEquipmentClickHandler = (equipment) => {
    let previousSerialNumber = equipment.serialNumber;
    let nextSerialNumber = "";
    if (isNaN(previousSerialNumber)) {
      nextSerialNumber = previousSerialNumber + "- Copy";
    } else {
      nextSerialNumber = String(Number(previousSerialNumber) + 1);
      nextSerialNumber = nextSerialNumber.padStart(
        previousSerialNumber.length,
        "0"
      );
    }
    let toSend = {
      serialNumber: nextSerialNumber,
      clientId: equipment.client ? equipment.client.id : null,
      stateId: equipment.state ? equipment.state.id : null,
      icon: equipment.icon.substring(equipment.icon.lastIndexOf("/") + 1),
      latitude: equipment.latitude,
      longitude: (Number(equipment.longitude) + 0.0001).toFixed(8).toString(),
      modelId: equipment.model.id,
    };
    // create Brand
    post("equipments", toSend)
      .then(() => {
        snackbarHandler(
          getTranslation("0122") &&
            ReplaceWord(
              getTranslation("0122"),
              "**NAME**",
              getTranslation("0071")
            ),
          "success"
        );
        setReloadEquipment(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: create equipments" + status);
      });
  };

  let equipmentElement = null;
  if (loadingEquipment && view === "list")
    equipmentElement = <LoadingList count={5} />;
  if (loadingEquipment && view === "map")
    equipmentElement = <LoadingBox width="100%" height="100%" />;
  if (!loadingEquipment && view === "list") {
    equipmentElement = equipmentsList.map((equipment) => (
      <EquipmentsDetailDashboard
        key={equipment.id}
        equipment={equipment}
        Select={() => equipmentSelectHandler(equipment)}
        Edit={() => editEquipmentClickHandler(equipment)}
        Delete={() => deleteEquipmentClickHandler(equipment.id)}
        Copy={() => copyEquipmentClickHandler(equipment)}
      />
    ));
  }
  if (!loadingEquipment && view === "map")
    equipmentElement = (
      <Maps
        list={marker}
        mapHeight={"100%"}
        Select={equipmentSelectHandler}
        MyLocationHandler={myLocationClickHandler}
        mapReload={mapReload}
      />
    );

  return (
    <Layout lang={lang}>
      <OuterWrapper height={wrapperHeight}>
        <BodyWrapperStyled fullHeight>
          <BoxStyled width="xs" col="2" noMargin>
            <CardStyled>
              <CardHeader>
                <FilterListOutlinedIcon className="mainIcon" />
                <div className="detail" data-translate="0076">
                  {getTranslation("0076")}
                  <span data-translate="0071,0077">
                    {getTranslation("0077") &&
                      ReplaceWord(
                        getTranslation("0077"),
                        "**LIST**",
                        getTranslation("0071").toLowerCase() + "s"
                      )}
                  </span>
                </div>
              </CardHeader>
              <CardBody height={boxHeight}>
                <FilterEquipment Filter={filterEquipmentChangeHandler} />
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled width="xl" col="2" noMargin>
            <CardStyled>
              <CardHeader>
                <DevicesOutlinedIcon className="mainIcon" />
                <div className="detail" data-translate="0071,0078">
                  {getTranslation("0071")}s
                  <span data-translate="0078">
                    {getTranslation("0078") &&
                      ReplaceWord(
                        getTranslation("0078"),
                        "**NUMBER**",
                        equipmentsList.length,
                        "**LISTNAME**",
                        getTranslation("0071").toLowerCase() + "s"
                      )}
                  </span>
                </div>
                <Tooltip text={getTranslation("0011")} placement="left">
                  <ViewListIcon
                    className={view === "list" ? "active" : ""}
                    onClick={listViewClickHandler}
                  />
                </Tooltip>
                <Tooltip text={getTranslation("0012")} placement="top">
                  <MapIcon
                    className={view === "map" ? "active" : ""}
                    onClick={mapViewClickHandler}
                  />
                </Tooltip>
              </CardHeader>
              <CardBody height={boxHeight} map={view === "map"}>
                {equipmentElement}
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <EquipmentDetailPopup
            ref={equipmentDetailRef}
            EditEquipment={editEquipmentClickHandler}
            DeleteEquipment={deleteEquipmentClickHandler}
            CopyEquipment={copyEquipmentClickHandler}
          />
          <AddEditEquipment
            ref={addEditEquipmentRef}
            UpdateSuccess={updateEquipmentSuccessHandler}
            clientList={clientList}
            stateList={stateList}
            iconList={iconList}
          />
          <DeletePopup
            ref={deleteEquipmentRef}
            DeleteSuccess={deleteEquipmentHandler}
          />
        </BodyWrapperStyled>
      </OuterWrapper>
    </Layout>
  );
};
